// =========================================================================
// Case-to-closure trace — projection only. Fork-owned (zone C), P5b lane.
//
// Ports two things from the demo:
//
//   * `renderWorkflow(key, phaseIdx)` — source line 2366. The hero figures,
//     the phase strip, the selected phase's rings and its task board.
//   * `genericRun(j)` — source line 3087. Every job on the board that is not
//     one of the three hand-written traces gets a trace generated from the
//     module it has reached: the ten modules become the ten phases, everything
//     before the job's module is complete, the job's own module carries its
//     percentage, everything after it is not started.
//
// Transformation layer (layer 3). No React, no fetching, no mutation.
//
// @see docs/logic/workflows/case-trace.md
// =========================================================================

import { BY_ID, MINES } from '../../data/core';
import type { OemJob } from '../../shared/oem-engine';
import { MODULES, type ModuleId } from '../../shared/seed/modules';
import {
  WORKFLOWS,
  WORKFLOW_KEYS,
  type Workflow,
  type WorkflowKey,
  type WorkflowPhase,
  type WorkflowStatus,
} from '../../shared/seed/workflows';

// -------------------------------------------------------------------------
// Generated runs
// -------------------------------------------------------------------------

/**
 * The demo's `sub` map inside `genericRun` (source line 3087), verbatim.
 *
 * Two or three steps per module, each with the task lines that describe what
 * that module does to a job. The generated trace concatenates the job's own
 * `next` sentence onto the first step of the module it has reached, which is
 * what makes a generated run say something specific rather than generic.
 */
const GENERIC_STEPS: Readonly<Record<ModuleId, readonly (readonly [string, readonly string[]])[]>> =
  {
    installed: [
      ['Registered', ['Serial, meters and contract in the Maximo register']],
      ['Systems linked', ['CMMS cross-reference · telemetry or shift-end feed']],
    ],
    demand: [
      ['Signal captured', ['Condition model scored the component']],
      ['Watch raised', ['Note on the condition register · in the S.O.S report']],
    ],
    orchestrate: [
      ['Case opened', ['Asset, customer and entitlement identified']],
      ['Routed', ['Priority set · specialists sequenced · owner named']],
    ],
    diagnose: [
      ['Finding', ['Failure mode ranked against the fleet history']],
      ['Prescription', ['Window, confidence, parts, tools, labour, safety']],
    ],
    commercial: [
      ['Entitlement', ['Warranty and contract checked on the record']],
      ['Who pays', ['Customer, campaign or goodwill · approval limit checked']],
    ],
    promise: [
      ['Draft work order', ['Drafted in the CMMS with the procedure attached']],
      ['Parts & people', ['Stock checked · technician proposed from roster + LMS']],
      ['Promise', ['Executable date inside the window · customer notified']],
    ],
    parts: [
      ['Requisition', ['Drafted for the planner · road or air by the window']],
      ['In transit', ['Tracked to the site store · reserved and kitted']],
    ],
    capacity: [
      ['Assigned', ['Skills, certificate, location and preference matched']],
      ['Remote expert', ['Booked where the job does not need a truck']],
    ],
    execute: [
      ['Work package', ['Steps, torques, isolation on the technician app']],
      ['Repair', ['Steps, safety checks, readings captured']],
      ['Test & handover', ['Post-repair test · customer sign-off']],
    ],
    closeout: [
      ['Close-out & cost', ['Parts, labour, serials, root cause coded']],
      ['Rating & claim', ['CSS · invoice or warranty claim']],
      ['Fleet rule', ['Lesson into the model, the PM plan or the stock minimum']],
    ],
  };

/** The demo's `modIdx` — where a job's stage sits in the poster order. */
const moduleIndex = (stage: string): number =>
  Math.max(0, MODULES.findIndex((m) => m.id === stage));

/**
 * The machine the demo falls back to on a fleet-level job (`IDS.L14`, which
 * resolves to `L-02` in the shipped pack — see `seed/workflows.ts`).
 */
const FALLBACK_MACHINE = 'L-02';

/** Demo line 3087. `genericRun` — a trace built from a job's own state. */
export function genericRun(job: OemJob): Workflow {
  const here = moduleIndex(job.stage);

  const phases: WorkflowPhase[] = MODULES.map((m, k) => {
    const status: WorkflowStatus = k < here ? 'done' : k === here ? 'now' : 'todo';
    const pct = k < here ? 100 : k === here ? job.pct : 0;

    const steps = GENERIC_STEPS[m.id].map(([name, tasks], q) => ({
      name,
      pct:
        status === 'done'
          ? 100
          : status === 'now'
            ? q === 0
              ? 100
              : q === 1
                ? Math.min(100, job.pct)
                : 0
            : 0,
      status:
        status === 'done'
          ? ('done' as const)
          : status === 'now'
            ? q <= 1
              ? ('now' as const)
              : ('todo' as const)
            : ('todo' as const),
      // The job's own next action joins the first step of its current module.
      tasks: k === here && q === 0 ? [...tasks, job.next] : [...tasks],
    }));

    return { name: m.short, pct, status, steps };
  });

  return {
    name: `${job.kind} — ${job.title}`,
    machine: job.machine ?? FALLBACK_MACHINE,
    ref: `${job.id} · ${MINES[job.mine].name}`,
    phases,
  };
}

// -------------------------------------------------------------------------
// Resolving the selected case
// -------------------------------------------------------------------------

/** What the scene renders, and where it came from. */
export interface ResolvedTrace {
  /** `'breakdown' | 'predictive' | 'seismic'`, or `job:<id>`. */
  readonly key: string;
  readonly workflow: Workflow;
  /** `true` for the three hand-written traces. */
  readonly worked: boolean;
}

/**
 * Resolve a `?case=` value. The demo's dispatch (source line 2366), in order:
 * a worked key wins; `job:<id>` generates a run for that job; anything else
 * falls back to `breakdown`.
 */
export function resolveTrace(
  key: string | null,
  jobs: readonly OemJob[] | null,
): ResolvedTrace {
  if (key && (WORKFLOW_KEYS as readonly string[]).includes(key)) {
    return { key, workflow: WORKFLOWS[key as WorkflowKey], worked: true };
  }
  if (key?.startsWith('job:') && jobs) {
    const job = jobs.find((j) => j.id === key.slice(4));
    if (job) return { key, workflow: genericRun(job), worked: false };
  }
  return { key: 'breakdown', workflow: WORKFLOWS.breakdown, worked: true };
}

/** The picker: the three worked traces first, then every job on the board. */
export interface TracePickerEntry {
  readonly value: string;
  readonly label: string;
  readonly worked: boolean;
}

export function tracePicker(jobs: readonly OemJob[] | null): TracePickerEntry[] {
  const worked = WORKFLOW_KEYS.map((k) => ({
    value: k as string,
    label: `${WORKFLOWS[k].name} · ${WORKFLOWS[k].ref}`,
    worked: true,
  }));
  if (!jobs) return worked;
  return [
    ...worked,
    ...jobs.map((j) => ({
      value: `job:${j.id}`,
      label: `${j.id} · ${j.kind} — ${j.title.slice(0, 54)}`,
      worked: false,
    })),
  ];
}

// -------------------------------------------------------------------------
// Header figures
// -------------------------------------------------------------------------

/** The four hero figures — demo `wf-hero` (source line 2372). */
export interface TraceHeader {
  /** Case number, the part of `ref` before the first ` · `. */
  readonly caseNo: string;
  /** Case name, truncated at 46 as the demo truncates it. */
  readonly caseName: string;
  readonly machine: string;
  /** `"<model> · <mine name>"`, or an em dash for an unknown machine. */
  readonly machineDetail: string;
  /** Mean phase percentage, rounded. */
  readonly overall: number;
  readonly phasesDone: number;
  readonly phaseCount: number;
}

export function traceHeader(workflow: Workflow): TraceHeader {
  const phases = workflow.phases;
  const fleet = BY_ID[workflow.machine];
  return {
    caseNo: (workflow.ref || '').split(' · ')[0] || '—',
    caseName: workflow.name.slice(0, 46),
    machine: workflow.machine || '—',
    machineDetail: fleet ? `${fleet.model} · ${MINES[fleet.mine].name}` : '—',
    overall: Math.round(phases.reduce((a, p) => a + p.pct, 0) / phases.length),
    phasesDone: phases.filter((p) => p.status === 'done').length,
    phaseCount: phases.length,
  };
}

/** The phase the page opens on: the first in progress, else the first. */
export const defaultPhaseIndex = (workflow: Workflow): number =>
  Math.max(0, workflow.phases.findIndex((p) => p.status === 'now'));

// -------------------------------------------------------------------------
// Task cards
// -------------------------------------------------------------------------

/** The eight technicians the demo's task renderer looks for by name. */
const TASK_NAMES = [
  'Dale Whitmore',
  'Ben Tuilagi',
  'Anders Lindqvist',
  'Marco Bellini',
  'Jorge Castillo',
  'Alicia Fenton',
  'Tom Reilly',
  'Tane Waititi',
] as const;

/** One task card, split as the demo splits it. */
export interface TraceTask {
  /** Text before the first ` · `, truncated at 60. */
  readonly lead: string;
  /** Everything after it, rejoined. */
  readonly detail: string;
  /** The technician the line names, or the demo's `'Agent-drafted'`. */
  readonly who: string;
  /** `'✓ done' | 'in progress' | 'queued'`, from the owning step. */
  readonly state: string;
  /** The demo tinted a task mentioning an agent or a draft. */
  readonly agent: boolean;
}

const STATE_LABEL: Readonly<Record<WorkflowStatus, string>> = {
  done: '✓ done',
  now: 'in progress',
  todo: 'queued',
};

/**
 * Demo line 2379. A task string split into its bold lead, its detail, the
 * person it names and the state it inherits from its step.
 */
export function traceTask(task: string, status: WorkflowStatus): TraceTask {
  const parts = task.split(' · ');
  const [lead, overflow] = splitLead(parts[0]);
  return {
    lead,
    // The demo discarded anything past 60 characters of the first segment,
    // which cut a generated task mid-word. The 60-character bold lead is
    // kept; the remainder continues on the detail line instead of vanishing.
    detail: [overflow, parts.slice(1).join(' · ')].filter(Boolean).join(' · '),
    who: TASK_NAMES.find((n) => task.includes(n)) ?? firstNameMatch(task) ?? 'Agent-drafted',
    state: STATE_LABEL[status],
    agent: /agent|draft/i.test(task),
  };
}

/**
 * Split a task's first segment into a bold lead of at most 60 characters and
 * whatever follows it, breaking on the last word boundary inside the limit.
 */
function splitLead(head: string): [string, string] {
  if (head.length <= 60) return [head, ''];
  const window = head.slice(0, 60);
  const space = window.lastIndexOf(' ');
  const cut = space > 30 ? space : 60;
  return [head.slice(0, cut).trimEnd(), head.slice(cut).trimStart()];
}

/**
 * The demo's regex has a second, shorter alternation (`Dale|Ben|Anders|…`)
 * that catches a bare first name. Kept so a line reading "Anders" alone still
 * attributes rather than falling through to "Agent-drafted".
 */
function firstNameMatch(task: string): string | null {
  const m = task.match(/\b(Dale|Ben|Anders|Marco|Jorge|Alicia|Tom|Tane)\b/);
  return m ? m[1] : null;
}

/** The module chip on the hero — the demo pins the trace to module 03. */
export const TRACE_MODULE: ModuleId = 'orchestrate';
